import { useEffect, useMemo, useState } from 'react';
import {
  Timestamp,
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/core/firebase';
import type {
  WorkoutAssignment,
  WorkoutTemplate,
  WorkoutTemplateItem,
} from '../types';
import type { TemplateItemDraft } from '../components/trainer/TemplateBuilder';

export function useTrainerTemplates(trainerId?: string) {
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [templateNotes, setTemplateNotes] = useState('');
  const [templateItems, setTemplateItems] = useState<TemplateItemDraft[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<WorkoutAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trainerId) return;

    const templateQuery = query(
      collection(db, 'workoutTemplates'),
      where('trainerId', '==', trainerId)
    );
    const assignmentQuery = query(
      collection(db, 'workoutAssignments'),
      where('trainerId', '==', trainerId)
    );

    const unsubTemplates = onSnapshot(templateQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutTemplate)
      );
      setTemplates(docs);
    });

    const unsubAssignments = onSnapshot(assignmentQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutAssignment)
      );
      setAssignments(docs);
    });

    return () => {
      unsubTemplates();
      unsubAssignments();
    };
  }, [trainerId]);

  const upcomingAssignments = useMemo(
    () =>
      [...assignments].sort(
        (a, b) =>
          (a.scheduledFor as Timestamp).toMillis() -
          (b.scheduledFor as Timestamp).toMillis()
      ),
    [assignments]
  );

  const addTemplateItem = (itemDraft: TemplateItemDraft, index?: number | null) => {
    if (!itemDraft.exerciseId) {
      setError('Choose an exercise to add to the template.');
      return;
    }
    setTemplateItems((prev) => {
      if (index !== null && index !== undefined && index >= 0 && index < prev.length) {
        const next = [...prev];
        next[index] = itemDraft;
        return next;
      }
      return [...prev, itemDraft];
    });
    setError(null);
  };

  const moveTemplateItem = (index: number, direction: 'up' | 'down') => {
    setTemplateItems((prev) => {
      const next = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const reorderTemplateItems = (oldIndex: number, newIndex: number) => {
    setTemplateItems((prev) => {
      if (
        oldIndex < 0 ||
        oldIndex >= prev.length ||
        newIndex < 0 ||
        newIndex >= prev.length
      ) {
        return prev;
      }
      const next = [...prev];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      return next;
    });
  };

  const startTemplateEdit = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setSelectedTemplateId(templateId);
    setTemplateName(tpl.name);
    setTemplateNotes(tpl.notes || '');
    setTemplateItems(tpl.items || []);
  };

  const resetTemplateDraft = () => {
    setSelectedTemplateId(null);
    setTemplateName('');
    setTemplateNotes('');
    setTemplateItems([]);
    setError(null);
  };

  const saveTemplate = async () => {
    if (!trainerId) {
      setError('Missing trainer id in auth claims.');
      return;
    }
    if (!templateName || templateItems.length === 0) {
      setError('Template needs a name and at least one item.');
      return;
    }
    setError(null);

    const items: WorkoutTemplateItem[] = templateItems.map((item, idx) => ({
      id: `${Date.now()}_${idx}`,
      exerciseId: item.exerciseId,
      order: idx,
      sets: item.sets || 3,
      reps: item.reps || 10,
      restSec: item.restSec || 60,
      weightSuggestion: item.weightSuggestion || null,
      customCues: item.customCues || '',
    }));

    const selectedTemplate = selectedTemplateId
      ? templates.find((template) => template.id === selectedTemplateId)
      : null;
    const saveTrainerWorkoutTemplate = httpsCallable<
      {
        templateId?: string;
        clientId?: string | null;
        name: string;
        notes: string;
        items: WorkoutTemplateItem[];
      },
      { success: boolean; templateId: string; clientId?: string | null }
    >(functions, 'saveTrainerWorkoutTemplate');

    await saveTrainerWorkoutTemplate({
      ...(selectedTemplateId ? { templateId: selectedTemplateId } : {}),
      clientId: selectedTemplate?.clientId || null,
      name: templateName,
      notes: templateNotes,
      items,
    });

    resetTemplateDraft();
  };

  return {
    addTemplateItem,
    assignments,
    error,
    moveTemplateItem,
    reorderTemplateItems,
    resetTemplateDraft,
    saveTemplate,
    selectedTemplateId,
    setTemplateItems,
    setTemplateName,
    setTemplateNotes,
    startTemplateEdit,
    templateItems,
    templateName,
    templateNotes,
    templates,
    upcomingAssignments,
  };
}
